import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Button,
  Chip,
  ClickAwayListener,
  CSSProperties,
  Paper,
  Popper,
  ScopedCssBaseline,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { SapStatus, VivaStatus } from '../../dtos.ts';
import { theme } from '../../theme/theme.ts';
import { determineStatusText } from '../../utils';
import { VaStatusChip } from './VaStatusChip';

type VaStatusSwitchProps = {
  readonly sapStatus: SapStatus | null;
  readonly vivaStatus: VivaStatus | null;
  onChange: (newValue: VivaStatus) => void;
  disabled: boolean;
  statusTransitions: VivaStatus[];
  readonly popperStyle?: CSSProperties;
};

export const VivaStatusSwitch = ({
  sapStatus,
  vivaStatus,
  onChange,
  disabled,
  statusTransitions,
  popperStyle,
}: VaStatusSwitchProps) => {
  const statusText = determineStatusText(sapStatus, vivaStatus);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (statusTransitions.length === 0) {
    return <VaStatusChip sapStatus={sapStatus} vivaStatus={vivaStatus} />;
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => setOpen((open) => !open)}
        ref={setAnchorEl}
        disabled={disabled}
        endIcon={<KeyboardArrowDown sx={{ fontSize: '20px !important' }} />}
      >
        <Typography variant="label" color="brand">
          {statusText}
        </Typography>
      </Button>
      <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ mt: 1 }} style={popperStyle}>
        <ScopedCssBaseline>
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper sx={{ py: 1, px: 0.5, display: 'grid', gap: 1, mt: 0.5 }} onClick={() => setOpen(false)}>
              {statusTransitions.map((newStatus) => (
                <StatusChangeRow key={newStatus} newStatus={newStatus} onClick={onChange} />
              ))}
            </Paper>
          </ClickAwayListener>
        </ScopedCssBaseline>
      </Popper>
    </>
  );
};

type StatusChangeRowProps<T> = {
  newStatus: T;
  onClick: (newStatus: T) => void;
};

const StatusChangeRow = function <T extends VivaStatus>({ newStatus, onClick }: StatusChangeRowProps<T>) {
  return (
    <Typography
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        px: 1.5,
        borderRadius: 1,
        '&:hover': { background: theme.palette.surface.brandLight, color: theme.palette.surface.brand },
      }}
      onClick={() => onClick(newStatus)}
    >
      Auf <Chip variant="tag" color="primary" label={determineStatusText(newStatus)} /> setzen
    </Typography>
  );
};
